<template>
  <div id="custom_input" class="flex w-full flex-col">
    <div
      :class="[
        'flex flex-row items-center',
        isError ? 'input_error' : '',
        types[type].css,
        cssClass,
      ]"
    >
      <ThemeImage
        v-if="icon"
        :class="[size, iconCSS]"
        :icon="icon"
        :alt="alt"
      />
      <input
        ref="inputRef"
        :type="format"
        v-model="inputValue"
        :placeholder="placeholder"
        :class="['w-full', types[type].text, 'outline-0']"
        :disabled="disabled"
        @input="handleInput"
        @blur="emit('blur', $event)"
        :autofocus="autoFocus"
        :min="format === 'number' ? min : undefined"
        :max="format === 'number' ? max : undefined"
        :step="format === 'number' && allowDecimals ? 'any' : undefined"
      />
      <div v-if="addAfterOn" class="mt-[2px]">{{ addAfterOn }}</div>
      <ThemeImage
        v-if="showClearIcon"
        :class="clearIconClasses"
        :icon="imageNames.CLOSE_GREY_FILLED"
        alt="clear input"
        @click.stop="clearInput"
      />
      <ThemeImage
        v-else-if="endIcon"
        :class="[endIconSize, endIconBackCSS]"
        :icon="endIcon"
        :alt="alt"
        @click="endIconClick"
      />
    </div>
    <div v-if="isError && errorMessage" class="error_message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup>
import {
  defineProps,
  defineEmits,
  toRefs,
  computed,
  ref,
  nextTick,
  onMounted,
} from "vue";
import { focusDelay } from "@/src/constants/commonConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  format: String,
  modelValue: String,
  placeholder: String,
  errorMessage: String,
  isError: Boolean,
  cssClass: String,
  disabled: Boolean,
  type: {
    type: String,
    default: "primary",
  },
  icon: String,
  size: String,
  iconCSS: String,
  alt: String,
  endIcon: String,
  endIconSize: String,
  endIconBackCSS: String,
  endIconClick: Function,
  onChange: Function,
  autoFocus: Boolean,
  addAfterOn: String,
  min: Number,
  max: Number,
  showClearButton: {
    type: Boolean,
    default: true,
  },
  allowDecimals: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "clear", "blur"]);

const { modelValue } = toRefs(props);
const inputRef = ref(null);

const showClearIcon = computed(() => {
  return props.showClearButton && inputValue.value;
});

const clearIconClasses = computed(() => {
  return [
    props.endIconSize || "size-[13px]",
    props.endIconBackCSS || "cursor-pointer",
    "mt-[1px] clear_icon",
  ];
});

const focus = () => {
  inputRef.value?.focus();
};

const select = () => {
  inputRef.value?.select();
};

const clearInput = () => {
  inputValue.value = "";
  if (props.onChange) {
    props.onChange("");
  }
  emit("clear");
  nextTick(() => {
    focus();
  });
};

const inputValue = computed({
  get: () => modelValue.value,
  set: (value) => {
    if (props.format === "number") {
      const numValue = Number(value);
      if (props.min !== undefined && numValue < props.min) {
        value = String(props.min);
      }
      if (props.max !== undefined && numValue > props.max) {
        value = String(props.max);
      }
    }
    emit("update:modelValue", value);
  },
});

const handleInput = (event) => {
  let value = event.target.value;
  if (props.format === "number" && value !== "") {
    value = props.allowDecimals ? parseFloat(value) : parseInt(value, 10);
  }
  inputValue.value = value;
  if (props.onChange) {
    props.onChange(value);
  }
};

const types = {
  primary: {
    css: "primary_input",
    text: "primary_input_field",
  },
  secondary: {
    css: "secondary_input",
    text: "secondary_input_field",
  },
};

onMounted(() => {
  if (props.autoFocus) {
    nextTick(() => {
      setTimeout(() => {
        focus();
      }, focusDelay);
    });
  }
});

defineExpose({ focus, select });
</script>

<style scoped>
.primary_input,
.secondary_input,
.input_error {
  display: flex;
  flex-direction: row;
  gap: 8px;
  border-radius: 8px;
  cursor: pointer;
  align-items: flex-start;
  align-self: stretch;
}

.primary_input {
  border: 1px solid var(--white-opacity-8);
  background: var(--white-opacity-4);
  padding: 8px 12px;
}

.primary_input_field {
  background: transparent;
  color: var(--foreground-rgb);
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.primary_input_field::placeholder {
  font-weight: 400;
  color: var(--white-opacity-20);
}

.secondary_input {
  border: 1px solid rgb(255 255 255 / 8%);
  background: var(--primary-button-bg-color);
  padding: 5px 8px;
}

.secondary_input_field {
  background: var(--primary-button-bg-color);
  color: var(--primary-button-fg-color);
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.input_error {
  border: 1px solid var(--error-color);
}

.error_message {
  color: var(--error-color);
  font-family: "Proxima Nova", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  margin-top: 4px;
}

.clear_icon {
  transition: filter 0.3s ease-in-out;
}

.clear_icon:hover {
  filter: invert(70%);
}

input:focus,
input:focus-visible {
  outline: none !important;
}
</style>
